import * as React from 'react';
import { useMemo } from 'react';
import * as ReactDOM from 'react-dom';
import * as R from 'ramda';
import { css } from '@emotion/core';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import LocalBtn from '../../localization/LocalBtn';
import { Str } from '../../localization/translationKeys';
import { Z_INDEX } from '../../globalStyle/zIndex';
import { COLORS } from '../../globalStyle/colors';
import { DevResource } from '../resources/models';
import LocalDiv from '../../localization/LocalDiv';
import { btnStyle } from '../../forms/styles/button';
import Msg from '../../localization/Msg';
import Spacer from '../../layout/Spacer';
import { DevPlanItem } from '../devPlan/models';
import { useAppState } from '../../core/redux/hooks';
import * as Localization from '../../localization/module';


const styles = {
  root:           css`
    justify-content: flex-end;
    position: absolute;
    top: 45px;
    z-index: ${Z_INDEX.COMPETENCY_LIST};
    background-color: ${COLORS.WHITE};
  `,
  right:          css`
    right: 10px;
  `,
  left:           css`
    left: 10px;
  `,
  list:           css`
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1px solid ${COLORS.LIGHT_GREY};
    height: 300px;
    overflow: auto;
  `,
  listItem:       css`
    padding: 5px 15px;
    border-bottom: 1px solid ${COLORS.LIGHTER_GREY}
  `,
  name:           css`
    justify-content: flex-start;
    margin-top: 12px;
  `,
  selectedName:   css`
    color: ${COLORS.MEDIUM_GREY};
  `,
  unselectedName: css`
  color: ${COLORS.DARK_GREY};
`,
  between:        css`
    justify-content: space-between;
  `,
  added:          css`
    margin-top: 12px;
  `,
  button:         css`
    & > span {
      padding: 0 12px;
    }
  `,
  spacer:         css`
    height: 10px;
  `,
};


type Props = {
  className?: string;
  competencies: DevResource[];
  devItems: DevPlanItem[];
  addItem: (competencyId: number) => void;
  deleteItem: (competencyId: number) => void;
}

const added = (id: number, devItems: DevPlanItem[]) => (
  devItems.filter(itm => itm.devItemCompetencyId === id).length > 0
);

/**
 * CompetencyList: a list of competencies that can be added to the dev plan
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CompetencyList: React.FC<Props> = props => {
  const {competencies, devItems, ...rest} = props;
  const isRtl = useAppState(state => Localization.isRtl(state.locale.textDirection));

  const competencyRows = useMemo(
    () => R.map( compCat => {
      const isAdded = added(compCat.competencyId, devItems);

      return (
        <li css={styles.listItem}>
          <LocalDiv css={[FLEX_ROW, styles.between]}>
            <LocalDiv css={[
                FLEX_COLUMN,
                styles.name,
                isAdded ? styles.selectedName : styles.unselectedName,
              ]}
            >
              {compCat.letterCode + '.' + compCat.name}
            </LocalDiv>
            <LocalDiv css={[FLEX_COLUMN]}>
              <LocalDiv css={[FLEX_ROW]}>
                <Spacer sizePx={120} css={styles.spacer} />
                {
                  isAdded && (
                    <>
                      <Msg str={Str.Added} css={styles.added}/>
                      <Spacer sizePx={10} />
                    </>
                  )
                }
                <LocalBtn
                  str={isAdded ? Str.Remove : Str.Add}
                  css={[
                    isAdded
                      ? btnStyle.transparent(COLORS.DARK_GREY)
                      : btnStyle.primary(COLORS.DARK_GREY),
                    styles.button,
                  ]}
                  onClick={
                    isAdded
                      ? () => rest.deleteItem(compCat.competencyId)
                      : () => rest.addItem(compCat.competencyId)
                  }
                />
              </LocalDiv>
            </LocalDiv>
          </LocalDiv>
        </li>
      )
      },
      competencies,
    ),
    [competencies, devItems, rest],
  );

  return (
    ReactDOM.createPortal(
      <div css={[FLEX_ROW, styles.root, isRtl ? styles.left : styles.right]}>
        <ul css={styles.list}>
          {competencyRows}
        </ul>
      </div>
      ,
      document.getElementById('root') || document.getElementById('react-root') || document.body,
    )
  )
};

export default CompetencyList;
